import React from "react";
import { NodeEdge } from "@hex-insights/app-modules";
import {
	ChildrenProps,
	ClassNameProps,
	Column,
	compareObjects,
	compareStringsAsc,
	Conditional,
	Else,
	If,
	makeClassName,
	mappedArray,
	Modal,
	Row,
	Rule,
	sort,
	StyleProps,
} from "@hex-insights/core";
import { CalendarEvent } from "../../../../Utilities";
import { CalendarEventFormat } from "../../../Format";
import { CalendarEventUtilities } from "../../../Utilities";
import * as CalendarEventResponseComponents from "../../CalendarEventResponse";
import { ControlledCalendarEventModal } from "../Modal";
import styles from "./styles.module.css";

export type ListCalendarEvent = Pick<
	CalendarEvent,
	"id" | "name" | "isAllDay" | "startDate" | "endDate" | "startTime" | "endTime" | "allowsResponses"
> & {
	// Note: should only contain responses for the current user
	calendarEventResponses: Pick<CalendarEvent["calendarEventResponses"][0], "response">[];
};

export type CalendarEventListProps = {
	calendarEventEdges: NodeEdge<ListCalendarEvent>[] | null | undefined;
	numDisplayed?: number;
	noEventsDisplay?: React.ReactNode;
};

export function CalendarEventList({
	calendarEventEdges,
	numDisplayed = 0,
	noEventsDisplay = "No Events",
}: CalendarEventListProps) {
	const sortedEventEdges = sort(
		calendarEventEdges ?? [],
		compareObjects(({ node }) => CalendarEventUtilities.startTimeDate(node).toISOString(), compareStringsAsc()),
	);
	const displayedEventEdges = numDisplayed ? sortedEventEdges.slice(0, numDisplayed) : sortedEventEdges;

	return (
		<Column justify="spaced-start" verticalSpacing="0.25rem">
			<Conditional>
				<If condition={displayedEventEdges.length === 0}>
					<UpcomingEventNoEventsView>{noEventsDisplay}</UpcomingEventNoEventsView>
					{mappedArray(Math.max(numDisplayed - 1, 0), (i) => (
						<React.Fragment key={i}>
							<FillerRule />
							<UpcomingEventFillerView />
						</React.Fragment>
					))}
				</If>
				<Else>
					{displayedEventEdges.map((e, i) => (
						<React.Fragment key={e.node.id}>
							<If condition={i > 0}>
								<Rule />
							</If>
							<UpcomingEventView calendarEvent={e.node} />
						</React.Fragment>
					))}
					{mappedArray(Math.max(numDisplayed - displayedEventEdges.length, 0), (i) => (
						<React.Fragment key={i}>
							<FillerRule />
							<UpcomingEventFillerView />
						</React.Fragment>
					))}
				</Else>
			</Conditional>
		</Column>
	);
}

type UpcomingEventViewProps = {
	calendarEvent: ListCalendarEvent;
} & Partial<ClassNameProps & StyleProps>;

function UpcomingEventView({ calendarEvent, className, style }: UpcomingEventViewProps) {
	const response = calendarEvent.calendarEventResponses[0];

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<div onClick={openModal} className={makeClassName(styles["upcoming-event"], className)} style={style}>
				<Row justify="space-between" align="center">
					<Column justify="spaced-start" verticalSpacing="0.25rem">
						<strong>{CalendarEventFormat.name(calendarEvent)}</strong>
						<span className={styles["upcoming-event__timestamp"]}>
							{calendarEvent.isAllDay
								? CalendarEventFormat.Fields.startDate(calendarEvent.startDate)
								: CalendarEventFormat.Fields.startTime(calendarEvent.startTime)}
						</span>
					</Column>

					<Conditional>
						<If condition={!calendarEvent.allowsResponses}>
							<span className={styles["upcoming-event__response"] + " " + styles["upcoming-event__response--none"]}>
								View
							</span>
						</If>
						<If condition={!!response}>
							{!!response && (
								<CalendarEventResponseComponents.ResponseBadge
									response={response.response}
									className={styles["upcoming-event__response"]}
								/>
							)}
						</If>
						<Else>
							<span className={styles["upcoming-event__response"] + " " + styles["upcoming-event__response--none"]}>
								RSVP
							</span>
						</Else>
					</Conditional>
				</Row>
			</div>

			<Modal.If condition={isModalOpen}>
				<ControlledCalendarEventModal calendarEventID={calendarEvent.id} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}

function UpcomingEventNoEventsView({ children }: ChildrenProps) {
	return (
		<div style={{ height: "55px" }} className={styles["upcoming-event--no-events"]}>
			<span className={styles["upcoming-event--no-events__text"]}>{children}</span>
		</div>
	);
}

function UpcomingEventFillerView() {
	return <div style={{ height: "55px" }}></div>;
}

function FillerRule() {
	return <div style={{ height: "1px" }}></div>;
}
